import { useState, useEffect } from "react"

import ReactRailsUJS from "react_ujs"

import Helpers from "@/unpoly/helpers"

export const useInput = ({ ref, enabled = true }) => {
  const changeEventQueue = useDispatchQueue({ dispatch: (e) => UnpolyEmitChangeEvent(e) })

  unpolyHelloRef({ ref, enabled })

  return {
    pauseEvents: changeEventQueue.block,
    playEvents: changeEventQueue.unblock,
    onChange: (event) => {
      ref.current && changeEventQueue.enqueue(Helpers.newChangeEvent(ref.current))
    },
  }
}

// Mark the given input as an unpoly reference
const unpolyHelloRef = ({ ref, enabled }) => {
  useEffect(() => {
    if (!enabled) return
    if (!ref.current) return
    UnpolyElementMounted(ref.current)
  }, [ref])
}

/**
 * React hook for creating a (blockable) DispatchQueue.
 *
 * Upon creation a dispatch function is provided.
 *
 * The dispatch function will be invoked for each element in the queue when the queue is unblocked.
 *
 * If the queue is blocked then elements accumulate until such a time that the queue is unblocked again.
 *
 * Dispatch Queue is useful if you want to enqueue events while a form control is
 * actively focused on and only dispatch when the control loses focus.
 *
 */
const useDispatchQueue = ({ dispatch }) => {
  const [isBlocked, setBlocked] = useState(false)
  const [queue, setQueue] = useState([])

  useEffect(() => {
    if (isBlocked || queue.length === 0) return

    queue.forEach((e) => {
      dispatch(e)
    })

    setQueue([])
  }, [isBlocked, queue])

  return {
    isBlocked,
    block: () => {
      setBlocked(true)
    },
    unblock: () => {
      setBlocked(false)
    },
    enqueue: (e) => {
      setQueue(queue.concat([e]))
    },
  }
}

/**
 * Publishes a change event for the targetElement
 */
const UnpolyEmitChangeEvent = (changeEvent) =>
  Helpers.withUnpoly((up) => {
    up.emit(changeEvent.target, changeEvent)
  })

const UnpolyElementMounted = (element) => Helpers.withUnpoly((up) => up.hello(element))

// TODO(unpoly) Extract the specific compilers to our "application"
const addCompilerForDynamicReactComponents = (up) => {
  /*
   * Mimic ReactRailsUJS.CLASS_NAME_ATTR selector
   *
   * This compiler must run before other compilers to ensure that input elements are present when
   * Unpoly looks for them.
   *
   * We have to patch the selector finding mechanism because ReactRailsUJS wants
   * a parent element.
   *
   * See for more information
   *  https://github.com/reactjs/react-rails/blob/ce356aeb82f011b6cb16428018ebaadc3e4a5553/react_ujs/index.js#L59-L63
   *
   *
   */
  up.compiler("div[data-react-class]", { priority: -1 }, (element, _data) => {
    const fauxSelector = { elements: [element] }

    const previousFindDOMNodes = ReactRailsUJS.findDOMNodes
    try {
      ReactRailsUJS.findDOMNodes = (searchSelector) => {
        if (searchSelector === fauxSelector) {
          return searchSelector.elements
        }
        return previousFindDOMNodes(searchSelector)
      }

      // console.log("Unpoly: ReactRailsUJS.mountComponents", element)

      ReactRailsUJS.mountComponents(fauxSelector)
    } finally {
      ReactRailsUJS.findDOMNodes = previousFindDOMNodes
    }

    return () => {
      // Triggers a warning, and may not be necessary, but I am not 100% sure.
      // console.log("Unpoly: ReactRailsUJS.unmountComponents", element)
      // ReactRailsUJS.unmountComponents(fauxParentSelector)
    }
  })
}

export const start = (options) => {
  /**
   * We had two options
   *
   * 1. Let ReactUJS handle the react component remounting
   *
   * 2. Use an Unpoly compiler to handle it
   *
   * We attemted (2), but because ReactUJS already handles a varienty of events
   * for mounting, we instead leave it to handle mounting and unmounting so we
   * decided to go with (1)
   *
   */

  ReactRailsUJS.handleEvent("up:fragment:inserted", ReactRailsUJS.handleMount)
  // previous approach: addCompilerForDynamicReactComponents(up)
}

export default { start }
